/** Salesforce Open CTI (Lightning) adapter. Wraps the callback-based `sforce.opencti`
 *  API in promises and maps it to our HostBridge. Open CTI is served BY the org
 *  (opencti_min.js) and injected into the softphone iframe; we lazy-load it and
 *  degrade to unavailable() when we're not actually inside Salesforce. */
import type { Contact } from "../telephony/types";
import type { CallLog, HostBridge, HostHandlers, ScreenPopResult } from "./hostBridge";

// Minimal shape of the bits of Open CTI we use (avoids a hard type dependency).
interface OpenCti {
  onClickToDial(args: { listener: (payload: { number: string; recordId?: string; recordName?: string; objectType?: string }) => void }): void;
  searchAndScreenPop(args: {
    searchParams: string; callType: string; deferred?: boolean;
    callback?: (r: { success: boolean; returnValue?: Record<string, unknown> }) => void;
  }): void;
  saveLog(args: { value: Record<string, unknown>; callback?: (r: { success: boolean }) => void }): void;
  setSoftphonePanelVisibility?(args: { visible: boolean }): void;
  enableClickToDial(args?: { callback?: () => void }): void;
}

declare global {
  interface Window { sforce?: { opencti?: OpenCti } }
}

const OPENCTI_SRC = "/support/api/62.0/lightning/opencti_min.js";

async function loadOpenCti(): Promise<OpenCti | null> {
  if (window.sforce?.opencti) return window.sforce.opencti;
  // Only meaningful inside a Salesforce iframe; try to load from the org origin.
  if (window === window.top) return null;
  try {
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = OPENCTI_SRC;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("opencti_min.js failed to load"));
      document.head.appendChild(s);
      setTimeout(() => reject(new Error("opencti load timeout")), 5000);
    });
  } catch {
    return null;
  }
  return window.sforce?.opencti ?? null;
}

export class SalesforceHostBridge implements HostBridge {
  readonly name = "salesforce";
  private cti: OpenCti | null = null;

  static async isPresent(): Promise<boolean> {
    return (await loadOpenCti()) != null;
  }

  async init(handlers: HostHandlers): Promise<void> {
    this.cti = await loadOpenCti();
    if (!this.cti) return;
    this.cti.enableClickToDial();
    this.cti.onClickToDial({
      listener: (p) => {
        const contact: Contact = { number: p.number, name: p.recordName, recordId: p.recordId };
        handlers.onClickToDial(contact);
      },
    });
  }

  screenPop(number: string, direction: "inbound" | "outbound", _interactionId?: string): Promise<ScreenPopResult | null> {
    if (!this.cti) return Promise.resolve(null);
    return new Promise((resolve) => {
      this.cti!.searchAndScreenPop({
        searchParams: number,
        callType: direction, // "inbound" | "outbound"
        deferred: false,
        callback: (r) => {
          if (!r.success || !r.returnValue) return resolve(null);
          // returnValue is a map of recordId → {Id, Name, RecordType, ...}. Take the first.
          const first = Object.values(r.returnValue)[0] as Record<string, unknown> | undefined;
          if (!first) return resolve(null);
          resolve({
            recordId: String(first.Id ?? first.RecordId ?? ""),
            name: (first.Name as string) ?? undefined,
            company: (first.Company as string) ?? (first.Account as string) ?? undefined,
          });
        },
      });
    });
  }

  saveCallLog(log: CallLog): Promise<void> {
    if (!this.cti) return Promise.resolve();
    return new Promise((resolve) => {
      this.cti!.saveLog({
        value: {
          entityApiName: "Task",
          Subject: `${log.direction === "inbound" ? "Inbound" : "Outbound"} call — ${log.name ?? log.number}`,
          Description: log.notes ?? "",
          CallType: log.direction,
          CallDurationInSeconds: log.durationSec,
          CallDisposition: log.disposition ?? log.outcome,
          Status: "Completed",
          ...(log.recordId ? { WhoId: log.recordId } : {}),
        },
        callback: () => resolve(),
      });
    });
  }

  setSoftphoneVisible(visible: boolean): void {
    this.cti?.setSoftphonePanelVisibility?.({ visible });
  }

  dispose(): void { /* Open CTI has no teardown */ }
}
